/**
 * Per-source display-color override, shared by the maps and the fleet timeseries.
 *
 * A team color of black is nearly invisible against the dark satellite basemap and the dark chart background,
 * so black is rendered as solid white instead. Two rules, either of which forces white:
 *   1. NZL is pinned white by name (its configured team color is black).
 *   2. Any source whose configured color parses as black.
 * This applies on every map — live, explore, and fleet, across both the SVG (LiveMultiBoatLayer /
 * useBoatAnimation) and Canvas2D GPU (GlBoatLayer / GlMapBoatLayer) renderers — and to the per-boat line and
 * legend swatch on the fleet timeseries. Every other source keeps its team color and the caller's default
 * outline, so nothing else changes.
 */

import { rgb as d3rgb } from "d3-color";

/** Solid white — used for both the fill and the outline of overridden sources. */
const WHITE = "#ffffff";

/** Sources pinned to a fixed marker look regardless of their configured team color (case-insensitive). */
const FORCED_WHITE_SOURCES = new Set(["NZL"]);

export interface BoatMarkerStyle {
  fill: string;
  stroke: string;
}

/**
 * True when a CSS color parses as black. Covers "#000", "#000000", "black" and "rgb(0,0,0)" in one test;
 * anything unparseable is NOT black, so a bad value is never silently whitened.
 */
export function isBlackColor(color: string | null | undefined): boolean {
  if (!color) return false;
  const c = d3rgb(String(color));
  if (!Number.isFinite(c.r) || !Number.isFinite(c.g) || !Number.isFinite(c.b)) return false;
  return c.r === 0 && c.g === 0 && c.b === 0;
}

/** True when this source is pinned white by name, regardless of its configured color. */
export function isForcedWhiteBoat(sourceName: string | null | undefined): boolean {
  return !!sourceName && FORCED_WHITE_SOURCES.has(String(sourceName).trim().toUpperCase());
}

/** True when this source should render white — either pinned by name, or its color is black. */
function shouldRenderWhite(sourceName: string | null | undefined, color: string): boolean {
  return isForcedWhiteBoat(sourceName) || isBlackColor(color);
}

/**
 * Resolve a boat marker's fill + outline.
 * @param sourceName    the source (boat) name, e.g. "NZL"
 * @param color         the source's normal fill (team color)
 * @param defaultStroke the outline the renderer would otherwise use — preserved for non-overridden sources
 */
export function resolveBoatMarkerStyle(
  sourceName: string | null | undefined,
  color: string,
  defaultStroke: string
): BoatMarkerStyle {
  if (shouldRenderWhite(sourceName, color)) {
    return { fill: WHITE, stroke: WHITE };
  }
  return { fill: color, stroke: defaultStroke };
}

/**
 * Resolve a source's display color — the map track (trail) and the fleet timeseries line/legend swatch.
 * Returns white for pinned sources (NZL) and for any black team color, so the line matches the white boat
 * marker; otherwise the source's own color is returned unchanged. Only affects source-colored tracks —
 * metric colorings (grade/wind/VMG) resolve their color elsewhere and are unaffected.
 */
export function resolveTrackColor(sourceName: string | null | undefined, color: string): string {
  return shouldRenderWhite(sourceName, color) ? WHITE : color;
}
